import { Component, Input, ChangeDetectionStrategy, inject, signal } from "@angular/core"
import { IconImageService } from "@shared/services/icon-image.service"

export type HelperTextVariant = "default" | "destructive" | "success"

let nextId = 0

@Component({
  selector: "flow-helper-text",
  exportAs: "flowHelperText",
  templateUrl: "./flow-helper-text.component.html",
  styleUrls: ["./flow-helper-text.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlowHelperTextComponent {
  public iconService = inject(IconImageService)

  @Input() variant: HelperTextVariant = "default"
  @Input() showIcon: boolean = true
  @Input() helperText: string = ""
  @Input() characterCount: number | null = null
  @Input() maxCount: number | null = null

  private readonly uid = nextId++
  public readonly textId = `flow-helper-text-${this.uid}`
  public readonly countId = `flow-helper-text-count-${this.uid}`

  public readonly flashSignal = signal(false)
  private flashTimeout: ReturnType<typeof setTimeout> | null = null

  public flash(): void {
    if (this.flashTimeout !== null) return
    this.flashSignal.set(true)
    this.flashTimeout = setTimeout(() => {
      this.flashSignal.set(false)
      this.flashTimeout = null
    }, 1000)
  }

  get isDestructive(): boolean {
    return this.variant === "destructive"
  }

  get isSuccess(): boolean {
    return this.variant === "success"
  }

  get icon(): string {
    return this.isSuccess ? this.iconService.Icon.check_1_5 : this.iconService.Icon.i_letter_1_5
  }

  get showCount(): boolean {
    return this.characterCount !== null && this.maxCount !== null
  }

  get textLiveSetting(): "assertive" | "polite" {
    return this.isDestructive ? "assertive" : "polite"
  }

  get isNearLimit(): boolean {
    if (this.characterCount === null || this.maxCount === null) {
      return false
    }
    return this.characterCount >= this.maxCount * 0.9
  }

  get countLiveSetting(): "polite" | "off" {
    return this.isNearLimit ? "polite" : "off"
  }
}
